/**
 * AutoCAD $INSUNITS values expressed as inches per drawing unit.
 *
 * DXFs from older software may omit $INSUNITS, and 0 explicitly means
 * unitless. Both cases intentionally fall back to 1 so existing inch-based
 * uploads keep working. Unknown future values receive the same safe fallback.
 */
const DXF_INCHES_PER_DRAWING_UNIT: Readonly<Record<number, number>> = {
    0: 1, // Unitless: preserve Prism's historical assumption of inches.
    1: 1,
    2: 12,
    3: 63_360,
    4: 1 / 25.4,
    5: 1 / 2.54,
    6: 100 / 2.54,
    7: 100_000 / 2.54,
    8: 0.000001,
    9: 0.001,
    10: 36,
    11: 1e-8 / 2.54,
    12: 1e-7 / 2.54,
    13: 0.0001 / 2.54,
    14: 10 / 2.54,
    15: 1_000 / 2.54,
    16: 10_000 / 2.54,
    17: 100_000_000_000 / 2.54,
    18: 14_959_787_070_000 / 2.54,
    19: 946_073_047_258_080_000 / 2.54,
    20: 3_085_677_581_491_367_300 / 2.54,
    21: (1_200 / 3_937) * (100 / 2.54),
    22: ((1_200 / 3_937) * (100 / 2.54)) / 12,
    23: (1_200 / 3_937) * (100 / 2.54) * 3,
    24: (1_200 / 3_937) * (100 / 2.54) * 5_280,
};

/**
 * Read the optional $INSUNITS HEADER variable from an ASCII DXF.
 * Returns null instead of throwing for older, malformed, or partial files.
 */
export function readDxfInsertionUnits(dxf: string): number | null {
    if (!dxf) return null;

    const lines = dxf.split(/\r?\n/);
    for (let index = 0; index + 3 < lines.length; index++) {
        if (lines[index].trim() !== '9' || lines[index + 1].trim().toUpperCase() !== '$INSUNITS') continue;

        // Header variables are code/value pairs. Stop at the next variable or
        // section marker so a malformed $INSUNITS cannot borrow another value.
        for (let valueIndex = index + 2; valueIndex + 1 < lines.length; valueIndex += 2) {
            const groupCode = lines[valueIndex].trim();
            if (groupCode === '9' || groupCode === '0') break;
            if (groupCode !== '70') continue;

            const unitCode = Number(lines[valueIndex + 1].trim());
            return Number.isInteger(unitCode) ? unitCode : null;
        }

        return null;
    }

    return null;
}

/** Return the scale needed to normalize a DXF drawing coordinate to inches. */
export function dxfInchesPerDrawingUnit(dxf: string): number {
    const unitCode = readDxfInsertionUnits(dxf);
    return unitCode === null ? 1 : DXF_INCHES_PER_DRAWING_UNIT[unitCode] ?? 1;
}

export function convertDxfLengthToInches(length: number, dxf: string): number {
    return length * dxfInchesPerDrawingUnit(dxf);
}

/**
 * Preserve the existing whole-inch approximation without allowing conversion
 * noise such as 304.8 / 25.4 = 12.000000000000002 to round up to 13.
 */
export function ceilDxfDimensionInches(length: number): number {
    if (!Number.isFinite(length) || length <= 0) return length;
    const tolerance = Math.max(1, Math.abs(length)) * 1e-9;
    return Math.ceil(length - tolerance);
}
